import { skipToken, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { useAuthzContext } from './AuthzContextProvider'
import { RecordPermissionsResponseError } from './errors'
import { isNotFoundBody } from './isNotFoundBody'
import { NO_RECORD_ASKED_QUERY_KEY, recordAuthorizationQueryKey } from './recordAuthorizationQuery'
import type { PermissionSetEntity, RecordPermissionsFor } from '@omnea-app/authz'

// 20 minutes. Freshness comes from a mutation invalidating the prefix, not from expiry.
const ACCESS_FRESHNESS_MS = 20 * 60 * 1000

type SettledRecordAuthorization<Entity extends PermissionSetEntity> =
  // The service does not answer for this record — the organisation is off it, or the record is one it
  // does not hold, which an organisation already enforcing still has for anything unmigrated.
  { entity: Entity; status: 'role' } | { entity: Entity; status: 'resolved'; permissions: RecordPermissionsFor<Entity> }

export type RecordAuthorization<Entity extends PermissionSetEntity> =
  | { entity: Entity; status: 'loading' }
  // The check failed, so nothing is permitted.
  | { entity: Entity; status: 'unavailable'; error: unknown }
  | SettledRecordAuthorization<Entity>

export type AnyRecordAuthorization = RecordAuthorization<PermissionSetEntity>

export type AskedRecord<Entity extends PermissionSetEntity> = { entity: Entity; recordId: string }

type RecordPermissionsAnswer<Entity extends PermissionSetEntity> = { entity: Entity; permissions: RecordPermissionsFor<Entity> }

const answersFor = <Entity extends PermissionSetEntity>(
  answer: RecordPermissionsAnswer<PermissionSetEntity>,
  entity: Entity
): answer is RecordPermissionsAnswer<Entity> => answer.entity === entity

const fetchRecordAuthorization = async <Entity extends PermissionSetEntity>(
  token: string,
  { entity, recordId }: AskedRecord<Entity>
): Promise<SettledRecordAuthorization<Entity>> => {
  const permissionsClient = new PermissionsHttpClient({ authorization: token })
  const { data: response, error } = await permissionsClient.GET('/entities/{entity}/{recordId}/permissions', {
    params: { path: { entity, recordId } },
  })
  if (isNotFoundBody(error)) return { entity, status: 'role' }
  throwIfApiError(error)
  if (response == null) throw new RecordPermissionsResponseError({ reason: 'noBody', entity, recordId })

  const { recordPermissions } = response
  if (!answersFor(recordPermissions, entity)) {
    throw new RecordPermissionsResponseError({
      reason: 'answeredForAnotherEntity',
      entity,
      recordId,
      answeredFor: recordPermissions.entity,
    })
  }
  return { entity, status: 'resolved', permissions: recordPermissions.permissions }
}

/** What the caller may do to one record, or null where there is no record to ask about. */
export function useRecordAuthorization<Entity extends PermissionSetEntity>(
  asked: AskedRecord<Entity>
): RecordAuthorization<Entity>
export function useRecordAuthorization<Entity extends PermissionSetEntity>(
  asked: AskedRecord<Entity> | null
): RecordAuthorization<Entity> | null
export function useRecordAuthorization<Entity extends PermissionSetEntity>(
  asked: AskedRecord<Entity> | null
): RecordAuthorization<Entity> | null {
  const { getToken, user } = useAuthenticatedUser()
  const authzContext = useAuthzContext()
  const entity = asked?.entity ?? null
  const recordId = asked?.recordId ?? null

  const { data, isError, error } = useQuery({
    queryKey:
      entity == null || recordId == null ? NO_RECORD_ASKED_QUERY_KEY : recordAuthorizationQueryKey(user.id, entity, recordId),
    queryFn:
      entity == null || recordId == null
        ? skipToken
        : async () => fetchRecordAuthorization(await getToken(), { entity, recordId }),
    enabled: authzContext.status === 'resolved',
    refetchOnWindowFocus: true,
    staleTime: ACCESS_FRESHNESS_MS,
    gcTime: ACCESS_FRESHNESS_MS,
  })

  return useMemo((): RecordAuthorization<Entity> | null => {
    if (entity == null) return null
    // A context that has not answered is not an organisation off the service: treating it as one shows every
    // affordance, then withdraws some when the answer lands.
    if (authzContext.status === 'loading') return { entity, status: 'loading' }
    if (authzContext.status === 'unavailable') return { entity, status: 'unavailable', error: authzContext.error }
    if (authzContext.status === 'role') return { entity, status: 'role' }
    // Read before isError so a failed background refetch keeps the last-known answer rather than
    // collapsing every affordance mid-session.
    if (data != null) return data
    if (isError) return { entity, status: 'unavailable', error }
    return { entity, status: 'loading' }
  }, [entity, authzContext, data, isError, error])
}
